/**
 * Product Sales Summary Cards Component
 *
 * Displays summary metrics for product sales report
 */

import React from 'react';
import {ProductSalesReportSummary} from '../models/productSalesReport.model';
import Card from '../../../Presentation/components/ui/Card';
import {AppText, AppCardTitle} from '../../../Presentation/components/ui/Typography';
import {formatCurrency, formatNumber} from '../../../Presentation/utils/numberUtils';

interface ProductSalesSummaryCardsProps {
  summary: ProductSalesReportSummary;
}

export const ProductSalesSummaryCards: React.FC<ProductSalesSummaryCardsProps> = ({summary}) => {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
      {/* Total Products Card */}
      <Card className="p-6">
        <div className="flex items-center justify-between">
          <div>
            <AppText className="text-sm text-gray-600 mb-1">إجمالي المنتجات</AppText>
            <AppCardTitle className="text-2xl font-bold text-gray-900">
              {formatNumber(summary.total_products)}
            </AppCardTitle>
          </div>
          <div className="p-3 bg-brand-100 rounded-lg">
            <svg className="w-6 h-6 text-brand-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
            </svg>
          </div>
        </div>
      </Card>

      {/* Total Quantity Sold Card */}
      <Card className="p-6">
        <div className="flex items-center justify-between">
          <div>
            <AppText className="text-sm text-gray-600 mb-1">إجمالي الكمية المباعة</AppText>
            <AppCardTitle className="text-2xl font-bold text-gray-900">
              {formatNumber(summary.total_quantity_sold)}
            </AppCardTitle>
          </div>
          <div className="p-3 bg-green-100 rounded-lg">
            <svg className="w-6 h-6 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
            </svg>
          </div>
        </div>
      </Card>

      {/* Total Revenue Card */}
      <Card className="p-6">
        <div className="flex items-center justify-between">
          <div>
            <AppText className="text-sm text-gray-600 mb-1">إجمالي الإيرادات</AppText>
            <AppCardTitle className="text-2xl font-bold text-brand-600">
              {formatCurrency(summary.total_revenue)}
            </AppCardTitle>
          </div>
          <div className="p-3 bg-purple-100 rounded-lg">
            <svg className="w-6 h-6 text-purple-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
        </div>
      </Card>

      {/* Average Unit Price Card */}
      <Card className="p-6">
        <div className="flex items-center justify-between">
          <div>
            <AppText className="text-sm text-gray-600 mb-1">متوسط سعر الوحدة</AppText>
            <AppCardTitle className="text-2xl font-bold text-gray-900">
              {formatCurrency(summary.average_unit_price)}
            </AppCardTitle>
          </div>
          <div className="p-3 bg-orange-100 rounded-lg">
            <svg className="w-6 h-6 text-orange-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
            </svg>
          </div>
        </div>
      </Card>
    </div>
  );
};

